import { FormEvent, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api, AdminGrade, AdminSchool, SchoolStatus } from '../api';
import { Layout, Loading, ErrorBox } from '../components/Layout';
import {
  EntitlementPicker,
  PickerValue,
  emptyPicker,
  entitlementsFromPicker,
  pickerFromEntitlements,
} from '../components/EntitlementPicker';

const field = 'w-full rounded-md border border-slate-300 px-3 py-2 text-sm';
const label = 'block text-sm font-medium text-slate-700 mb-1';

/** yyyy-mm-dd for <input type="date">, or '' when there is no expiry. */
function dateInput(iso: string | null): string {
  return iso ? new Date(iso).toISOString().slice(0, 10) : '';
}

export function AdminSchoolEdit() {
  const { id = '' } = useParams();
  const [school, setSchool] = useState<AdminSchool | null>(null);
  const [grades, setGrades] = useState<AdminGrade[] | null>(null);
  const [picker, setPicker] = useState<PickerValue>(emptyPicker);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        const [s, g, ent] = await Promise.all([
          api.admin.school(id),
          api.admin.grades(),
          api.admin.entitlements(id),
        ]);
        setSchool(s);
        setGrades(g);
        setPicker(pickerFromEntitlements(ent));
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [id]);

  const saveDetails = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);
    setNote(null);
    setSaving(true);
    const f = new FormData(e.currentTarget);
    const str = (k: string) => (f.get(k) as string).trim();

    try {
      const updated = await api.admin.updateSchool(id, {
        name: str('name'),
        contactName: str('contactName') || undefined,
        phone: str('phone') || undefined,
        email: str('email') || undefined,
        seatLimit: str('seatLimit') ? Number(str('seatLimit')) : undefined,
        licenseEnd: str('licenseEnd')
          ? new Date(`${str('licenseEnd')}T23:59:59Z`).toISOString()
          : undefined,
      });
      setSchool(updated);
      setNote('Details saved.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status: SchoolStatus) => {
    setError(null);
    setNote(null);
    try {
      setSchool(await api.admin.updateSchool(id, { status }));
      setNote(`School is now ${status}.`);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const saveEntitlements = async () => {
    setError(null);
    setNote(null);
    setSaving(true);
    try {
      const res = await api.admin.setEntitlements(id, entitlementsFromPicker(picker));
      setNote(`Entitlements saved (${res.count} grant(s)).`);
      // Re-read rather than trust local state: the server normalizes (a whole
      // grade drops its per-subject rows), so what was sent is not always what
      // is now stored.
      setPicker(pickerFromEntitlements(await api.admin.entitlements(id)));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (error && !school) {
    return (
      <Layout>
        <ErrorBox message={error} />
      </Layout>
    );
  }
  if (!school || !grades) {
    return (
      <Layout>
        <Loading />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="mb-6">
        <Link to="/admin/schools" className="text-sm text-slate-500 hover:text-slate-900">
          ← Schools
        </Link>
        <h1 className="text-xl font-semibold text-slate-900 mt-1">{school.name}</h1>
        <p className="text-sm text-slate-500">
          {school.status} · {school.activeUsers}
          {school.seatLimit != null ? ` / ${school.seatLimit}` : ' / ∞'} seats used
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorBox message={error} />
        </div>
      )}
      {note && (
        <p className="mb-4 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
          {note}
        </p>
      )}

      <form onSubmit={(e) => void saveDetails(e)} className="space-y-4 mb-8">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={label} htmlFor="name">
              School name
            </label>
            <input
              id="name"
              name="name"
              required
              defaultValue={school.name}
              className={field}
            />
          </div>
          <div>
            <label className={label} htmlFor="contactName">
              Contact name
            </label>
            <input
              id="contactName"
              name="contactName"
              defaultValue={school.contactName ?? ''}
              className={field}
            />
          </div>
          <div>
            <label className={label} htmlFor="phone">
              Phone
            </label>
            <input id="phone" name="phone" defaultValue={school.phone ?? ''} className={field} />
          </div>
          <div>
            <label className={label} htmlFor="email">
              Contact email
            </label>
            <input
              id="email"
              name="email"
              type="email"
              defaultValue={school.email ?? ''}
              className={field}
            />
          </div>
          <div>
            <label className={label} htmlFor="seatLimit">
              Seat limit
            </label>
            <input
              id="seatLimit"
              name="seatLimit"
              type="number"
              min={1}
              defaultValue={school.seatLimit ?? ''}
              className={field}
            />
          </div>
          <div>
            <label className={label} htmlFor="licenseEnd">
              Licence ends
            </label>
            <input
              id="licenseEnd"
              name="licenseEnd"
              type="date"
              defaultValue={dateInput(school.licenseEnd)}
              className={field}
            />
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <button
            type="submit"
            disabled={saving}
            className="px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
          >
            Save details
          </button>
          {school.status === 'ACTIVE' ? (
            <button
              type="button"
              onClick={() => void setStatus('SUSPENDED')}
              className="px-4 py-2 rounded-md border border-amber-300 text-amber-800 text-sm hover:bg-amber-50"
            >
              Suspend
            </button>
          ) : (
            <button
              type="button"
              onClick={() => void setStatus('ACTIVE')}
              className="px-4 py-2 rounded-md border border-green-300 text-green-800 text-sm hover:bg-green-50"
            >
              Reactivate
            </button>
          )}
        </div>
      </form>

      <section>
        <h2 className="text-sm font-semibold text-slate-900 mb-1">Entitlements</h2>
        <p className="text-xs text-slate-500 mb-3">
          What this school may see. A grade left at “No access” is hidden entirely.
        </p>
        <EntitlementPicker grades={grades} value={picker} onChange={setPicker} />
        <button
          type="button"
          onClick={() => void saveEntitlements()}
          disabled={saving}
          className="mt-4 px-4 py-2 rounded-md bg-slate-900 text-white text-sm hover:bg-slate-800 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save entitlements'}
        </button>
      </section>
    </Layout>
  );
}
